/**
 * Root metadata for the Apache-2.0 replacement of `@/ee/whitelabeling`
 * (Omnyqra/sim#14), after the Apache version from before the move to `ee/`
 * (`lib/branding/metadata.ts` at `9c3fd1f7af^`), with the assets that exist
 * today under `public/`; the preview image is the Omnyqra one. See `SPEC.md` (WL-004).
 */
import type { Metadata } from 'next'
import { getBaseUrl } from '@/lib/core/utils/urls'
import { getBrandConfig } from '@/ee/whitelabeling/brand-config'

export function generateBrandedMetadata(override: Partial<Metadata> = {}): Metadata {
  const brand = getBrandConfig()
  const description = `${brand.name} is the AI workspace where teams build, deploy, and manage AI agents.`
  const image = brand.logoUrl || '/brand/omnyqra/og.png'

  return {
    title: { template: `%s | ${brand.name}`, default: brand.name },
    description,
    applicationName: brand.name,
    authors: [{ name: brand.name }],
    creator: brand.name,
    publisher: brand.name,
    referrer: 'origin-when-cross-origin',
    metadataBase: new URL(getBaseUrl()),
    openGraph: {
      type: 'website',
      locale: 'en_US',
      url: getBaseUrl(),
      title: brand.name,
      description,
      siteName: brand.name,
      images: [{ url: image, alt: brand.name }],
    },
    twitter: { card: 'summary_large_image', title: brand.name, description, images: [image] },
    manifest: '/manifest.webmanifest',
    icons: {
      icon: [
        { url: '/favicon/favicon-16x16.png', sizes: '16x16', type: 'image/png' },
        { url: '/favicon/favicon-32x32.png', sizes: '32x32', type: 'image/png' },
        {
          url: brand.faviconUrl || '/favicon/favicon.svg',
          type: brand.faviconUrl ? undefined : 'image/svg+xml',
        },
      ],
      apple: '/favicon/apple-touch-icon.png',
      shortcut: brand.faviconUrl || '/favicon/favicon.ico',
    },
    appleWebApp: { capable: true, statusBarStyle: 'default', title: brand.name },
    formatDetection: { telephone: false },
    ...override,
  }
}
